import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	Inject,
	OnDestroy,
	OnInit,
	signal,
	ViewContainerRef,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
	AlarisButtonComponent,
	AlarisCardComponent,
	AlarisComplexTableComponent,
	AlarisCtxActionComponent,
	AlarisEnumEntry,
	AlarisExpandedCellContextDirective,
	AlarisIconDirective,
	AlarisLinkComponent,
	AlarisLoadingDirective,
	AlarisLoadingStubDirective,
	AlarisNoFilterResultComponent,
	AlarisOptions2Component,
	AlarisOverflowFaderDirective,
	AlarisPaginationComponent,
	AlarisPBACDirective,
	AlarisProfileService,
	AlarisRowActionsDirective,
	AlarisSelect2Component,
	AlarisSettingsManagerComponent,
	AlarisTableComponent,
	AlarisTableSettingsService,
	AlarisTableStateService,
	AlarisTooltipDirective,
	DEFAULT_BUTTONS,
	EditPanelWidth,
	EnumsMapperService,
	PROFILE_SERVICE_INJECTOR,
	RowActionSimple,
	SharedTemplatesService,
	TABLE_STATE_SERVICE_INJECTOR,
	TableCellInput,
	TableFiltersIndicator,
} from '@campaign-portal/components-library';
import { CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { FilterType, SortDirection } from '@campaign-portal/namespace/common/rpc.params';
import { EntityField } from '@campaign-portal/namespace/common/entityField';
import { CampaignEntity, CP_PERMISSIONS } from '@shared/types';
import { CampaignAction, CampaignsService } from '../services/campaigns.service';
import { CampaignFieldsService } from './campaign-fields.service';
import { CampaignsTemplatesComponent } from './campaigns-templates/campaigns-templates.component';
import { CampaignFromFileComponent } from '../campaign-from-file/campaign-from-file.component';
import { CampaignWizardService } from '../campaign-wizard/campaign-wizard.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { exist } from '@campaign-portal/namespace/common/id';
import { ChannelsService, SendersService } from '@shared/services';
import { IdObject } from '@campaign-portal/namespace/common/idObject';
import { Channel } from '@campaign-portal/namespace/entities/channels/specs';
import { NamedObject } from '@campaign-portal/namespace/common/namedObject';
import { CampaignUtilsService } from '@shared/services/campaign-utils.service';
import { FormControl } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { CdkContextMenuTrigger } from '@angular/cdk/menu';

@Component({
	selector: 'app-campaign-list',
	templateUrl: './campaign-list.component.html',
	styleUrls: [
		'../../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'./campaign-list.component.scss',
	],
	providers: [
		CampaignFieldsService,
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService },
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlarisCardComponent,
		TranslateModule,
		AlarisButtonComponent,
		AlarisIconDirective,
		AlarisLinkComponent,
		AlarisPBACDirective,
		AlarisTableComponent,
		AlarisLoadingDirective,
		AlarisOverflowFaderDirective,
		AlarisPaginationComponent,
		AlarisLoadingStubDirective,
		AlarisTooltipDirective,
		AlarisNoFilterResultComponent,
		AlarisOptions2Component,
		AlarisSelect2Component,
		AlarisExpandedCellContextDirective,
		AlarisRowActionsDirective,
		NgTemplateOutlet,
		AlarisCtxActionComponent,
		CdkContextMenuTrigger,
	],
	standalone: true,
})
export class CampaignListComponent
	extends AlarisComplexTableComponent<CampaignEntity<exist>>
	implements OnDestroy, AfterViewInit, OnInit
{
	readonly CP_PERMISSIONS = CP_PERMISSIONS;
	readonly statuses: (CampaignStatus | null)[] = [null, ...Object.values(CampaignStatus)];
	readonly statusControl = new FormControl<CampaignStatus | null>(null);
	readonly expandedRows = signal<boolean[]>([]);
	readonly campaignIdsFromRecurrentHistory = new Set<exist>();

	override readonly rowActions: RowActionSimple<CampaignEntity<exist>>[] = [
		{
			icon: 'icon-play',
			label: 'actions.start',
			action: this.start.bind(this),
			shown: (campaign): boolean => campaign?.canStart() ?? false,
		},
		{
			icon: 'icon-play',
			label: 'actions.resume',
			action: this.resume.bind(this),
			shown: (campaign): boolean => campaign?.canResume() ?? false,
		},
		{
			icon: 'icon-arrow-move-list',
			dynamicLabel: this.dynamicRepeatLabel.bind(this),
			action: this.repeat.bind(this),
			shown: (campaign): boolean => campaign?.canRepeat() ?? false,
		},
		{
			icon: 'icon-pause-circle',
			label: 'actions.pause',
			action: this.pause.bind(this),
			shown: (campaign): boolean => campaign?.canPause() ?? false,
		},
	];

	override dropDownRowActions: RowActionSimple<CampaignEntity<exist>>[] = [
		{
			icon: '',
			label: 'actions.details',
			action: (campaign): void => this.openDetailsPanel(campaign),
		},
		{
			icon: 'icon-play',
			label: 'actions.start',
			action: this.start.bind(this),
			shown: (campaign): boolean => campaign?.canStart() ?? false,
		},
		{
			icon: 'icon-play',
			label: 'actions.resume',
			action: this.resume.bind(this),
			shown: (campaign): boolean => campaign?.canResume() ?? false,
		},
		{
			icon: 'icon-pause-circle',
			label: 'actions.pause',
			action: this.pause.bind(this),
			disabled: (campaign): boolean => !campaign?.canPause(),
		},
		{
			icon: 'deprecated/stop',
			label: 'actions.cancel',
			action: this.cancel.bind(this),
			disabled: (campaign): boolean => !campaign?.canCancel(),
		},
		{
			icon: 'icon-arrow-move-list',
			dynamicLabel: this.dynamicRepeatLabel.bind(this),
			action: this.repeat.bind(this),
			disabled: (campaign): boolean => !campaign?.canRepeat(),
			shown: (campaign): boolean => !campaign?.isFromFile(),
		},
		{
			icon: '',
			label: 'actions.delete',
			action: this.delete.bind(this),
			disabled: (campaign): boolean => !campaign?.canDelete(),
			highlight: true,
			separator: true,
		},
	];
	override ctxActions: RowActionSimple<CampaignEntity<exist>>[] = this.dropDownRowActions;
	readonly tSettingsMap = computed(() => {
		return new Map(this.tSettings());
	});

	override readonly id = 'campaign-list';
	disabledRows: boolean[] = [];

	constructor(
		public campaignsService: CampaignsService,
		public campaignFieldsService: CampaignFieldsService,
		public override tableSettingsService: AlarisTableSettingsService,
		public sharedTemplates: SharedTemplatesService<TableCellInput<CampaignEntity<exist>>>,
		private readonly viewRef: ViewContainerRef,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly enums: EnumsMapperService,
		private readonly cw: CampaignWizardService,
		private readonly channelsService: ChannelsService,
		private readonly senderIdsService: SendersService,
		private readonly campaignUtils: CampaignUtilsService,
		@Inject(PROFILE_SERVICE_INJECTOR) public profileService: AlarisProfileService
	) {
		super(campaignsService, campaignFieldsService, tableSettingsService, TableTypes.CAMPAIGN_LIST, DEFAULT_BUTTONS);
		this.viewRef.createComponent(CampaignsTemplatesComponent);

		if (!this.profileService.allowed([CP_PERMISSIONS.CAMPAIGNS_E])) {
			this.removeActions();
		}
	}

	get isFiltersApplied(): boolean {
		return this.state.isFiltersApplied || !!this.statusControl.value;
	}

	displayStatus = (status: CampaignStatus | null): string => {
		return status === null ? 'campaigns.allStatuses' : 'enums.' + status;
	};

	start(campaign: CampaignEntity<exist> | undefined): void {
		if (!campaign) {
			return;
		}
		this.campaignsService
			.update({
				...campaign,
				info: {
					...campaign.info,
					scheduled: new Date().toISOString(),
				},
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe();
	}

	cancel(campaign: CampaignEntity<exist> | undefined): void {
		if (!campaign) {
			return;
		}
		this.campaignsService.action(CampaignAction.CANCEL, campaign).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	delete(campaign: CampaignEntity<exist> | undefined): void {
		if (!campaign) {
			return;
		}
		this.campaignsService.openDialog('Delete', campaign).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	pause(campaign: CampaignEntity<exist> | undefined): void {
		if (!campaign) {
			return;
		}
		this.campaignsService.action(CampaignAction.PAUSE, campaign).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	resume(campaign: CampaignEntity<exist> | undefined): void {
		if (!campaign) {
			return;
		}
		this.campaignsService.action(CampaignAction.RESUME, campaign).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	repeat(campaign: CampaignEntity<exist> | undefined): void {
		if (!campaign) {
			return;
		}
		this.cw.repeat(campaign);
	}

	removeActions(): void {
		this.dropDownRowActions = this.ctxActions = [
			{
				icon: '',
				label: 'actions.details',
				action: (campaign): void => this.openDetailsPanel(campaign as CampaignEntity<exist>),
			},
		];
	}

	override dblClickRowAction(campaign?: CampaignEntity<exist>): void {
		this.openDetailsPanel(campaign);
	}

	override applyHeaders(tHeaders: EntityField[]): void {
		super.applyHeaders(tHeaders);
		const channelList$ = new BehaviorSubject<(Channel<exist> | NamedObject)[]>([]);
		this.channelsService.list$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((list) => {
			channelList$.next([...list, { name: 'campaigns.cascade' }]);
		});
		this.setRefBooksInFields({
			status: { list$: new BehaviorSubject<AlarisEnumEntry[]>(this.enums.transform(CampaignStatus)) },
			campaignChannel: { list$: channelList$ },
			campaignSenders: { list$: this.senderIdsService.list$, loading$: this.senderIdsService.loading$ },
		});
	}

	override applyFilter(event: TableFiltersIndicator): void {
		super.applyFilter(event);

		const filters = this.state.readParams.Filters;
		if (filters) {
			for (const f of filters) {
				if (f.Field === 'campaignSenders') {
					f.Value = (f.Value as IdObject<exist>[]).map((s) => s.id);
				}
				if (f.Field === 'campaignChannel') {
					f.Value = (f.Value as Channel<exist>[]).map((ch) =>
						ch.name === 'campaigns.cascade' ? 'CASCADE' : ch.channelType
					);
				}
			}
		}
		if (this.statusControl.value) {
			this.state.readParams.Filters?.push({
				Field: 'status',
				Type: FilterType.IN,
				Value: [this.statusControl.value],
			});
		}
	}

	override ngOnInit(): void {
		this.state
			.sorting()
			.set('shortUrl', { enabled: false, value: SortDirection.EMPTY })
			.set('campaignSenders', { enabled: false, value: SortDirection.EMPTY });

		this.state
			.filters()
			.set('progress', { enabled: false })
			.set('shortUrl', { enabled: false })
			.set('status', { enabled: false });

		super.ngOnInit();
		if (this.route.snapshot.queryParamMap.get('mode') === 'fromFile') {
			this.import();
		}

		this.campaignsService.forceLoad$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.refresh();
		});

		this.statusControl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this._applyFilter(this.state.filters()));
	}

	override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		this.templates = this.sharedTemplates.templates;
		this.cd.markForCheck();
	}

	override load(): void {
		this.saveState();
		this.loading$.next(true);
		this.campaignsService
			.read(this.state.readParams)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				this.tRows = resp.Data;
				this.total = resp.Total;
				this.disabledRows = this.tRows.map((c) => c.isDisabledRow());
				this.loading$.next(false);
				this.showLoadingStub = false;
				this.showNoFilterResult = this.isFiltersApplied && this.tRows.length === 0;
				if (this.isFiltersApplied) {
					const expanded = this.tRows.map((campaign) => !!campaign.recurrentHistory?.length);
					this.expandedRows.set(expanded);
				}
				this.campaignIdsFromRecurrentHistory.clear();
				for (const campaign of this.tRows) {
					const recurrentIds = (campaign.recurrentHistory ?? []).map((c) => c.id);
					recurrentIds.forEach((id) => this.campaignIdsFromRecurrentHistory.add(id));
				}
				const campaignIdParam = this.route.snapshot.queryParamMap.get('campaignId');
				if (campaignIdParam !== null) {
					this.loadCampaignById(Number(campaignIdParam));
				}
				this.cd.markForCheck();
			});
		this.cd.markForCheck();
	}

	override refresh(): void {
		this.load();
	}

	override _clearFilter(): void {
		this.statusControl.reset(null, { emitEvent: false });
		super._clearFilter();
	}

	override editTableSettings(): void {
		this.editPanel.open(AlarisSettingsManagerComponent, EditPanelWidth.MD, {
			tHeads: this.tHeaders,
			tSettings: this.tSettings,
			creationFieldAvailable: false,

			tSettingsChange: this.tSettingsChange,
			headEvent: this.tHeadEvent,
		});
	}

	override import(): void {
		this.editPanel.open(CampaignFromFileComponent, EditPanelWidth.MD, {});
	}

	override create(): void {
		this.router.navigate(['campaigns', 'campaign-wizard']);
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.completeRefBooksInFields(['status', 'campaignChannel']);
	}

	private openDetailsPanel(campaign?: CampaignEntity<exist>): void {
		if (campaign) {
			this.campaignsService.openDetailsPanel(campaign);
		}
	}

	private dynamicRepeatLabel(campaign?: CampaignEntity): string {
		const channels = campaign?.getTrafficTypes() ?? [];
		const isCampaignPreModerated = channels.some((channel) => this.campaignUtils.isPreModeratedChannel(channel));
		return isCampaignPreModerated ? 'actions.sendRequest' : 'actions.repeat';
	}

	private loadCampaignById(campaignId: number): void {
		this.campaignsService
			.read({
				Filters: [
					{
						Field: 'id',
						Type: FilterType.EXACT,
						Value: campaignId,
					},
				],
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				if (resp.Data.length > 0) {
					this.openDetailsPanel(resp.Data[0]);
				}
				this.clearCampaignIdQueryParam();
			});
	}

	private clearCampaignIdQueryParam(): void {
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { campaignId: null },
			queryParamsHandling: 'merge',
		});
	}
}
